import { difference, mapValues } from 'lodash-es';
import { DateTime } from 'luxon';
import { normalize, schema } from 'normalizr';
import { actions as clientActions } from 'redux/modules/clients';
import {
  activateProject,
  archiveProject,
  archiveProjectsForClient,
  projectSaved,
  removeProject,
} from 'redux/modules/projects.types';
import { removeUser } from 'redux/modules/users.types';
import { paginationFactory } from 'redux/reusable/pagination';
import { xhrStatusFactory } from 'redux/reusable/xhrStatus';
import type { Forecast } from 'routes/forecast/types/forecast';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import type { State } from './forecast.types';
import {
  clearAllForecasts,
  clearForecastDraft,
  clearQuery,
  deleteForecastId,
  deleteForecastIds,
  FORECAST_PAGE_LIMIT,
  fetchedForecast,
  fetchedForecasts,
  forecastXhrModule,
  setForecastDraft,
  setPopoverPrefInSession,
  setQuery_,
  updateForecastDraft,
  withBatchSelectionsReducer,
  withPaginationReducer,
} from './forecast.types';

const forecastSchema = new schema.Entity('forecast');
const forecastArraySchema = new schema.Array(forecastSchema);

const { withXhrStatusReducer } = forecastXhrModule;

interface ForecastNormalized {
  entities: {
    forecast: {
      [id: number]: Forecast;
    };
  };
  result: Array<number>;
}

export const initialState: State = {
  draft: null,
  entities: {},
  result: [],
  xhrStatus: xhrStatusFactory(),
  pagination: paginationFactory({
    limit: FORECAST_PAGE_LIMIT,
    order: 'desc',
    page: 1,
    sort: 'from',
  }),
  selection: { forecastIds: [], completedState: null },
  query: {
    projectIds: [],
    userIds: [],
    teamIds: null,
    showCompleted: false,
    onlyShowProjectAssignees: false,
    since: DateTime.now().minus({ weeks: 2 }).toISODate(),
    until: DateTime.now().plus({ weeks: 4 }).toISODate(),
    dateSpan: 'custom',
  },
  didSetPopoverPrefInSession: false,
};

const reducer = reducerWithInitialState<State>(initialState)
  .case(clearForecastDraft, state => ({
    ...state,
    draft: null,
  }))
  .case(setForecastDraft, (state, payload) => ({
    ...state,
    draft: payload,
  }))
  .case(updateForecastDraft, (state, payload) => {
    const { draft } = state;
    if (!draft) {
      return state;
    }
    return {
      ...state,
      draft: { ...draft, ...payload },
    };
  })
  .case(fetchedForecasts, (state, payload) => {
    const { entities, result }: ForecastNormalized = normalize(payload, forecastArraySchema);

    return {
      ...state,
      entities: { ...state.entities, ...entities.forecast },
      result: [...new Set([...state.result, ...result])],
    };
  })
  .case(fetchedForecast, (state, payload) => {
    const id = payload.temporaryId ?? payload.id;

    return {
      ...state,
      entities: {
        ...state.entities,
        [id]: payload,
      },
      result: [...new Set([...state.result, id])],
    };
  })
  .case(clearAllForecasts, state => ({
    ...state,
    entities: {},
    result: [],
  }))
  .case(deleteForecastIds, (state, payload) => {
    const newEntities = { ...state.entities };
    payload.forEach(id => {
      delete newEntities[id];
    });

    return {
      ...state,
      entities: newEntities,
      result: difference(state.result, payload),
    };
  })
  .case(deleteForecastId, (state, payload) => {
    const { [payload]: remove, ...remainingForecasts } = state.entities;
    return {
      ...state,
      entities: remainingForecasts,
      result: state.result.filter(id => id !== payload),
    };
  })
  .caseWithAction(projectSaved, (state, action) => {
    // Incase of color or name change, we need to update the project in all forecasts
    const { project } = action.payload;
    const updatedEntities = { ...state.entities };
    Object.values(updatedEntities).forEach(forecast => {
      if (forecast.project?.id === project.id) {
        updatedEntities[forecast.id] = { ...forecast, project };
      }
    });
    return {
      ...state,
      entities: updatedEntities,
    };
  })
  .caseWithAction(clientActions.savedClient, (state, action) => {
    // Incase of color change, we need to update the client in all forecasts
    const { client } = action.payload;
    const updatedEntities = { ...state.entities };
    Object.values(updatedEntities).forEach(forecast => {
      if (forecast.project?.client?.id === client.id) {
        const project = { ...forecast.project, client };
        updatedEntities[forecast.id] = { ...forecast, project };
      }
    });
    return {
      ...state,
      entities: updatedEntities,
    };
  })
  .cases([archiveProject, removeProject], (state, payload) => {
    const allForecasts = state.result.map(r => state.entities[r]);
    const forecastsForProject = allForecasts.filter(forecast => forecast?.project?.id === payload);
    const newEntities = { ...state.entities };
    forecastsForProject.forEach(forecast => {
      delete newEntities[forecast.id];
    });

    return {
      ...state,
      entities: newEntities,
      result: difference(
        state.result,
        forecastsForProject.map(f => f.id)
      ),
    };
  })
  .case(archiveProjectsForClient, (state, payload) => {
    const projectIds = Object.keys(payload);
    const allForecasts = state.result.map(r => state.entities[r]);

    const allForecastsToRemove = projectIds.reduce((prev: Array<Forecast>, id: string) => {
      const forecastsForProject = allForecasts.filter(forecast => forecast?.project?.id === +id);
      return [...prev, ...forecastsForProject];
    }, []);

    const newEntities = { ...state.entities };
    allForecastsToRemove.forEach(forecast => {
      delete newEntities[forecast.id];
    });
    return {
      ...state,
      entities: newEntities,
      result: difference(
        state.result,
        allForecastsToRemove.map(f => f.id)
      ),
    };
  })
  .case(removeUser, (state, payload) => {
    // if we're removing a user, manually go in and unassign their tasks
    const userId = payload;
    const nextEntities = mapValues(state.entities, forecast => {
      if (forecast.users.find(u => u.id === userId)) {
        const newUsers = forecast.users.filter(u => u.id !== userId);
        return { ...forecast, users: newUsers };
      } else {
        return forecast;
      }
    });
    return {
      ...state,
      entities: nextEntities,
    };
  })
  .case(activateProject, state => {
    return {
      ...state,
      // if we're activating a project, it's possible it may also have tasks associated with it
      // so we unset our xhrStatus state, invalidating our cache
      xhrStatus: xhrStatusFactory(),
    };
  })
  .case(setPopoverPrefInSession, (state, payload) => ({
    ...state,
    didSetPopoverPrefInSession: payload,
  }))
  .case(setQuery_, (state, payload) => ({
    ...state,
    query: { ...state.query, ...payload },
  }))
  .case(clearQuery, state => ({
    ...state,
    query: initialState.query,
  }));

export default withXhrStatusReducer(withPaginationReducer(withBatchSelectionsReducer(reducer)));
